import { type RJSFSchema } from '@rjsf/utils'
import { isPlainObject } from './mergePatch'
import { parametersValidator } from './rjsf/validator'
import zocksUiMetaSchema from './zocksUi.schema.json'

export interface ZocksUiHeading {
  title: string
  description?: string
}

export interface ZocksUi {
  widget?: 'textarea'
  order?: (string | ZocksUiHeading)[]
  uniqueField?: string
}

export const ZOCKS_UI_KEYS = ['widget', 'order', 'uniqueField'] as const satisfies readonly (keyof ZocksUi)[]

type ZocksUiKey = (typeof ZOCKS_UI_KEYS)[number]

const isSchema = (value: unknown): value is RJSFSchema => isPlainObject(value)

const metaProperties: unknown = zocksUiMetaSchema.properties

const keySchemaOf = (key: ZocksUiKey): RJSFSchema => {
  const keySchema = isPlainObject(metaProperties) ? metaProperties[key] : undefined
  if (!isSchema(keySchema)) {
    throw new Error(`zocksUi.schema.json has no "${key}" property`)
  }
  return keySchema
}

const valueSchemas: Record<string, RJSFSchema> = Object.fromEntries(
  ZOCKS_UI_KEYS.map((key) => [key, { type: 'object', properties: { [key]: keySchemaOf(key) } }]),
)

export const warnZocksUi = (message: string) => {
  if (import.meta.env.DEV) {
    console.warn(`zocksUi: ${message}`)
  }
}

const isZocksUiKey = (key: string): key is ZocksUiKey => (ZOCKS_UI_KEYS as readonly string[]).includes(key)

const isValidValue = (key: ZocksUiKey, value: unknown): boolean => {
  const { errors, validationError } = parametersValidator.rawValidation(valueSchemas[key], { [key]: value })
  const valid = !validationError && (errors ?? []).length === 0
  if (!valid) {
    warnZocksUi(`invalid "${key}" ignored: ${JSON.stringify(value)}`)
  }
  return valid
}

const isValidatedZocksUi = (value: unknown): value is ZocksUi => isPlainObject(value) && Object.keys(value).every(isZocksUiKey)

export const zocksUiOf = (schema: unknown): ZocksUi => {
  const raw = isPlainObject(schema) && isPlainObject(schema.zocksUi) ? schema.zocksUi : {}
  Object.keys(raw)
    .filter((key) => !isZocksUiKey(key))
    .forEach((key) => warnZocksUi(`unknown key "${key}" ignored`))
  const validated = Object.fromEntries(ZOCKS_UI_KEYS.filter((key) => key in raw && isValidValue(key, raw[key])).map((key) => [key, raw[key]]))
  return isValidatedZocksUi(validated) ? validated : {}
}
